import { useState } from 'react';

import BlankField from '../../../components/BlankField/BlankField';
import CleanButton from '../../../components/CleanButton/CleanButton';
import Column from '../../../components/Column/Column';
import Columns from '../../../components/Columns/Columns';
import JsonForm from '../../../components/Form/JsonForm';
import Paragraph from '../../../components/Paragraph/Paragraph';
import Row from '../../../components/Row/Row';
import Stack from '../../../components/Stack/Stack';
import SubmitButton from '../../../components/SubmitButton/SubmitButton';
import Text from '../../../components/Text/Text';
import TextField from '../../../components/TextField/TextField';
import Title from '../../../components/Title/Title';
import UncaughtErrors from '../../../components/UncaughtErrors/UncaughtErrors';
import useTrans from '../../../hooks/ui/useTrans';
import ValidationErrors from '../../../types/UI/ValidationErrors';

type ForgotPasswordFormProps = {
    onCancel: () => void;
    onComplete: () => void;
    actionUrl: string;
    layout?: 'stacked' | 'inline';
};

const ForgotPasswordForm = ({
    onCancel,
    onComplete,
    actionUrl,
    layout = 'inline',
}: ForgotPasswordFormProps) => {
    const trans = useTrans('CustomerArea.Shared.ForgotPasswordForm.');
    const [errors, setErrors] = useState<ValidationErrors>();
    const handleSuccess = () => {
        setErrors(undefined);
        onComplete();
    };

    const explanation = <Paragraph subdued>{trans('Explanation')}</Paragraph>;
    const usernameField = (
        <TextField
            flex={1}
            name="username"
            errors={errors?.username}
            label={trans('UsernameLabel')}
            autoComplete="username"
        />
    );
    const submitButton = (
        <SubmitButton fullWidth size="large">
            {trans('SubmitButton')}
        </SubmitButton>
    );
    const backButton = (
        <Row horizontal="right">
            <CleanButton onClick={onCancel}>
                <Text subdued size="small">
                    {trans('CancelButtonLabel')}
                </Text>
            </CleanButton>
        </Row>
    );

    const fields =
        layout === 'inline' ? (
            <Columns collapseBelow="tablet" gap="large">
                <Column width="2/5">{explanation}</Column>
                <Column width="2/5">
                    <Stack space="small">
                        {usernameField}
                        {backButton}
                    </Stack>
                </Column>
                <Column width="1/5">
                    <BlankField label=" ">{submitButton}</BlankField>
                </Column>
            </Columns>
        ) : (
            <>
                {explanation}
                {usernameField}
                {backButton}
                {submitButton}
            </>
        );

    return (
        <JsonForm
            method="post"
            action={actionUrl}
            onSuccess={handleSuccess}
            onError={(response) => setErrors(response?.errors)}
        >
            <Stack>
                <Title lineHeight="small">{trans('Title')}</Title>

                {fields}

                <UncaughtErrors errors={errors} exclude={['username']} />
            </Stack>
        </JsonForm>
    );
};

export default ForgotPasswordForm;
